(ns app.domain.conversation.model
  (:require [app.domain.business.model :as business-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.party.model :as party-model]
            [app.domain.review.model :as review-model]
            [app.domain.user.authority :as authority]
            [app.domain.workspace.model :as workspace-model]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]))

(def ^{:arglists '([workspace])} workspace-threads
  (child/child-entities-of-type-fn :entity.type/conversation-thread))

(def ^{:arglists '([conversation-thread])} thread-conversations
  (child/child-entities-of-type-fn :entity.type/conversation))

(defn with-roles [role-keys users]
  (->> users
       distinct
       (remove nil?)
       (map #(identity {:user % :roles (set role-keys)}))))

(defn review-users [review]
  (->> review
       :review/acceptances
       (map :entity/creator)
       (cons (:review/assignee review))))

(defn get-assignee [ws-entity group]
  (->> [(-> ws-entity :workspace/assignee)]
       (filter #(authority/authority-in-group? % group))))

(defn get-reviewers [phase-entities group]
  (->> phase-entities
       (mapcat review-model/application-reviews)
       (mapcat review-users)
       (filter #(authority/authority-in-group? % group))))

(defn get-booked-inspectors [inspections group]
  (->> inspections
       (mapcat (partial eu/descendants :entity.type/booking))
       (map :booking/assignee)
       (filter #(authority/authority-in-group? % group))))

(defn get-visited-inspectors [inspections group]
  (->> inspections
       (mapcat (partial eu/descendants :entity.type/inspection-visit))
       (mapcat :inspection.visit/inspectors)
       (filter #(authority/authority-in-group? % group))))

(defn party-users [party]
  (->> (concat
         (when-some [user (:party/user party)]
           [user])
         (some-> party :party/business business-model/business-users))
       distinct))

(defn get-parties [phase-entities]
  (->> phase-entities
       (mapcat party-model/phase-parties)
       (filter #(and (party-model/active? %)
                     (seq (party-users %))))))

(defn party-recipients [parties]
  (->> parties
       (mapcat (fn [{:party/keys [business party-roles] :as party}]
                 (map (fn [user]
                        (cond-> {:party party
                                 :user  user
                                 :roles (set party-roles)}
                          business (assoc :business business)))
                      (party-users party))))))

(defn get-msg-initiators [threads group]
  ;; users that have sent a message on the workspace, e.g. uninvolved authority
  ;; that messages an applicable authority should become available recipient for
  ;; replies
  (->> threads
       (mapcat thread-conversations)
       (concat threads)
       (keep :entity/creator)
       (filter #(authority/authority-in-group? % group))))

(defn format-party-recipients [party-recipients]
  (map #(select-keys % [:business :roles :user]) party-recipients))

(defn format-recipients [recipients]
  (reduce
    (fn [options user]
      (let [id (-> user :user :db/id)]
        (if (some? (get options id))
          (update-in options [id :roles] #(into % (:roles user)))
          (assoc options id user))))
    {}
    recipients))

(defn available-recipients-with-roles
  "Fetch available message recipients for the workspace. Notice that the individual get functions check if authority
   users are still working for the municipality via authority/authority-in-group? which can only check groups besides
   the current user's in the backend, so it is not recommended calling these functions from the frontend for message
   recipients."
  [ws-entity]
  (let [group              (group-model/get-authority-group ws-entity)
        assignee           (->> (get-assignee ws-entity group)
                                (with-roles [:assignee/assignee]))
        phase-entities     (workspace-model/phase-entities ws-entity)
        reviewers          (->> (get-reviewers phase-entities group)
                                (with-roles [:review/assignee]))
        inspections        (->> phase-entities
                                (mapcat inspection-model/phase-inspections))
        booked-inspectors  (->> (get-booked-inspectors inspections group)
                                (with-roles [:inspection/inspector]))
        visited-inspectors (->> (get-visited-inspectors inspections group)
                                (with-roles [:inspection/inspector]))
        parties            (->> (get-parties phase-entities)
                                party-recipients
                                format-party-recipients)
        threads            (workspace-threads ws-entity)
        msg-initiators     (->> (get-msg-initiators threads group)
                                (with-roles [:conversation/participant]))]
    (format-recipients (concat
                         assignee
                         reviewers
                         booked-inspectors
                         visited-inspectors
                         parties
                         msg-initiators))))
